import type { StaffPaymentQuoteViewResponse } from "@lafayette/schemas";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { deepLinks } from "../../navigation/deep-links";
import { useAreaHref } from "../../navigation/use-offering";
import { usePaymentCapabilities } from "../../payments/access";
import { PaymentStatusBadge, StaffOnly } from "../../payments/components/parts";
import {
  IssuePaymentDialog,
  RecordQuoteDialog,
  SupersedeQuoteDialog,
} from "../../payments/components/QuoteDialogs";
import { describePaymentError } from "../../payments/errors";
import { formatMinor, shortFingerprint } from "../../payments/format";
import {
  approvalBasisLabels,
  label,
  quoteStatusLabels,
  supplierEvidenceLabels,
} from "../../payments/labels";
import { paymentKeys, useRequestQuotes } from "../../payments/queries";
import { useOptionalPaymentsRuntime, usePaymentsRuntime } from "../../payments/runtime";
import { Amount } from "../../ui/amount";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Notice } from "../../ui/notice";
import { Panel as TitledCard } from "../../ui/panel";
import { StatusBadge } from "../../ui/status-badge";
import { emptyNoteClass, hintClass, linkClass } from "../components/parts";
import { formatInstant } from "../format";

// The quote and payment panel of a request page (CHT-757). It shows the quotes of the request, newest first, with the payment request each
// one backs, and offers three commands: record a quote, replace the approved one, and ask the customer to pay it. The commands are for the
// request owner or a supervisor, on a request that is in progress (the API judges the relationship again). The panel never shows an amount
// field for a PAYMENT: the one typed amount is the supplier's own quote. The supplier evidence reference is staff only.
//
// The panel draws nothing when the host gives it no payments runtime, and nothing for a staff role that cannot read payments.

type Props = Readonly<{
  requestId: string;
  requestState: string;
  /** The version of the concierge request the page last read. */
  requestVersion: number;
  /** Whether this staff member may quote: the owner, a supervisor, or unknown (then the server decides). */
  mayQuote: boolean;
  /** Reads the request again after a command (a replaced quote may return the request to IN_PROGRESS). */
  onChanged: () => void;
}>;

export function PaymentQuoteSection(props: Props) {
  const runtime = useOptionalPaymentsRuntime();
  const { capabilities, isLoading } = usePaymentCapabilities();
  if (runtime === null || isLoading || !capabilities.readQuotes) return null;
  return <Panel {...props} />;
}

type Open =
  | Readonly<{ kind: "record" }>
  | Readonly<{ kind: "supersede" | "issue"; quote: StaffPaymentQuoteViewResponse }>
  | null;

function Panel({ requestId, requestState, requestVersion, mayQuote, onChanged }: Props) {
  const { capabilities } = usePaymentCapabilities();
  const { now } = usePaymentsRuntime();
  const queryClient = useQueryClient();
  const quotes = useRequestQuotes(requestId, true);
  const [open, setOpen] = useState<Open>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const list = quotes.data?.quotes ?? [];
  const current = list.find((quote) => quote.status === "APPROVED") ?? null;
  const inProgress = requestState === "IN_PROGRESS";
  const canAct = capabilities.recordQuote && mayQuote;
  const expired = current !== null && current.validUntil.getTime() <= now().getTime();
  const reload = async () => {
    await queryClient.invalidateQueries({ queryKey: paymentKeys.all });
    onChanged();
  };
  const finish = (text: string) => {
    setOpen(null);
    setNotice(text);
    void reload();
  };

  return (
    <TitledCard id="quote-panel-title" title="Quote and payment">
      <StaffOnly>
        The customer sees the amount, the terms and the payment, never the supplier evidence.
      </StaffOnly>
      {notice === null ? null : (
        <Notice tone="success" role="status">
          <p>{notice}</p>
        </Notice>
      )}
      {quotes.isPending ? (
        <p role="status">Loading the quotes…</p>
      ) : quotes.isError && quotes.data === undefined ? (
        <Notice tone="warning" role="alert">
          <p>{describePaymentError(quotes.error).message}</p>
          <Button variant="outline" size="sm" onClick={() => void quotes.refetch()}>
            Try again
          </Button>
        </Notice>
      ) : (
        <>
          {current === null ? (
            <p className={emptyNoteClass}>There is no approved quote for this request.</p>
          ) : (
            <QuoteCard quote={current} expired={expired} />
          )}
          <div className="flex flex-wrap gap-2">
            {current === null ? (
              <Button
                disabled={!canAct || !inProgress}
                aria-describedby={canAct && inProgress ? undefined : "quote-panel-why"}
                onClick={() => {
                  setNotice(null);
                  setOpen({ kind: "record" });
                }}
              >
                Record a supplier quote
              </Button>
            ) : (
              <>
                {current.paymentRequest === null && capabilities.issueQuoteRequest ? (
                  <Button
                    disabled={!canAct || !inProgress || expired}
                    aria-describedby={
                      canAct && inProgress && !expired ? undefined : "quote-panel-why"
                    }
                    onClick={() => {
                      setNotice(null);
                      setOpen({ kind: "issue", quote: current });
                    }}
                  >
                    Ask the customer to pay
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  disabled={!canAct}
                  aria-describedby={canAct ? undefined : "quote-panel-why"}
                  onClick={() => {
                    setNotice(null);
                    setOpen({ kind: "supersede", quote: current });
                  }}
                >
                  Replace this quote
                </Button>
              </>
            )}
          </div>
          {canAct && inProgress ? null : (
            <p id="quote-panel-why" className={hintClass}>
              {!capabilities.recordQuote
                ? "Your role cannot record quotes."
                : !mayQuote
                  ? "Only the request owner or a supervisor can quote."
                  : "A quote needs a request that is in progress."}
            </p>
          )}
          {list.filter((quote) => quote !== current).length === 0 ? null : (
            <details className="grid gap-3">
              <summary className="min-h-11 cursor-pointer text-sm font-semibold compact:min-h-8">
                Earlier quotes ({list.filter((quote) => quote !== current).length})
              </summary>
              {list
                .filter((quote) => quote !== current)
                .map((quote) => (
                  <QuoteCard key={quote.id} quote={quote} expired={false} old />
                ))}
            </details>
          )}
        </>
      )}

      {open?.kind === "record" ? (
        <RecordQuoteDialog
          requestId={requestId}
          requestVersion={requestVersion}
          onDone={() => finish("The quote is recorded and approved.")}
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === "supersede" ? (
        <SupersedeQuoteDialog
          quote={open.quote}
          onDone={(result) =>
            finish(
              `The quote was replaced.${result.cancelledPayment ? " The old payment request was cancelled." : ""}${result.issued ? " The customer was asked to pay the new quote." : ""}`,
            )
          }
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open?.kind === "issue" ? (
        <IssuePaymentDialog
          quote={open.quote}
          onDone={() => finish("The customer was asked to pay this quote.")}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </TitledCard>
  );
}

function QuoteCard({
  quote,
  expired,
  old = false,
}: Readonly<{ quote: StaffPaymentQuoteViewResponse; expired: boolean; old?: boolean }>) {
  // The link opens the Payments area, which is on only where a real provider runs (CHT-1981): the offering gives no address otherwise.
  const areaHref = useAreaHref();
  const paymentHref =
    quote.paymentRequest === null
      ? null
      : areaHref("payments", deepLinks.payment(quote.paymentRequest.id));
  return (
    <article
      className={`grid gap-1.5 rounded-md border border-border p-3.5 [&_p]:m-0 ${old ? "opacity-85" : ""}`}
    >
      <p className="flex flex-wrap items-center gap-2.5 text-[30px] leading-[34px]">
        <Amount>{formatMinor(quote.amountMinor, quote.currency)}</Amount>{" "}
        <Badge>{label(quoteStatusLabels, quote.status)}</Badge>
        {expired ? <StatusBadge tone="warning">Expired</StatusBadge> : null}
      </p>
      <p>{quote.description}</p>
      <p>
        <strong>The customer reads:</strong> {quote.customerTerms ?? "No terms were recorded."}
      </p>
      <p className={hintClass}>
        Valid until {formatInstant(quote.validUntil.toISOString())} · approved by{" "}
        {label(approvalBasisLabels, quote.approvalBasis)} · fingerprint{" "}
        {shortFingerprint(quote.fingerprint)}
      </p>
      <p className={hintClass}>
        Staff only: {label(supplierEvidenceLabels, quote.supplierEvidenceKind)}, “
        {quote.supplierEvidenceReference}”
      </p>
      {quote.supersedesQuoteId === null ? null : (
        <p className={hintClass}>Replaces an earlier quote.</p>
      )}
      {quote.supersededByQuoteId === null ? null : (
        <p className={hintClass}>Replaced by a newer quote.</p>
      )}
      {quote.paymentRequest === null ? null : (
        <p className="flex flex-wrap items-center gap-1.5">
          Payment request: <PaymentStatusBadge status={quote.paymentRequest.status} />{" "}
          {paymentHref === null ? null : (
            <Link className={linkClass} to={paymentHref}>
              Open the payment
            </Link>
          )}
        </p>
      )}
    </article>
  );
}
